import type { MoveList } from 'chessboard/model/move-list/types';
import type JCE from '@chesscom/js-chess-engine';
import type { GameAPI } from 'chessboard/types';
import type { EventHandlers } from 'components/move-list/state-machine/types';
import type { Board } from 'chessboard/model/board/types';
import type { StoreActions, Store, NodeLimits } from 'components/move-list/store/types';
import type { Game } from 'chessboard/model/game/types';
import type { Data } from 'src/utils/svelte/data';
import type { Queue } from 'utils/queue/config/types';
import type { EffectTypes } from 'chessboard/plugins/effects/config/types';
import type { ContextMenu } from '../components/context-menu/config/types';

export const TIMESTAMP_SINGLE_DIGIT_WIDTH = 7;

/** Narrowing function using type predicate */
export function isCommentRowContent(
  content: MoveListComponent.Row['content'],
): content is MoveListComponent.Comment {
  return 'comment' in content;
}

/** Narrowing function using type predicate */
export function isMovesRowContent(
  content: MoveListComponent.Row['content'],
): content is MoveListComponent.MoveListNode[] {
  return Array.isArray(content) && (content[0]?.node !== undefined || content.length === 0);
}

/** Narrowing function using type predicate */
export function isCommentRow(
  row: MoveListComponent.Row,
): row is MoveListComponent.Row<MoveListComponent.Comment> {
  return isCommentRowContent(row.content);
}

/** Narrowing function using type predicate */
export function isDataCommentRow(
  row: Data<MoveListComponent.Row>,
): row is Data<MoveListComponent.Row<MoveListComponent.Comment>> {
  return isCommentRow(row.get());
}

/** Narrowing function using type predicate */
export function isMovesRow(
  row: MoveListComponent.Row,
): row is MoveListComponent.Row<MoveListComponent.MoveListNode[]> {
  return isMovesRowContent(row.content);
}

/** Narrowing function using type predicate */
export function isDataMovesRow(
  row: Data<MoveListComponent.Row>,
): row is Data<MoveListComponent.Row<MoveListComponent.MoveListNode[]>> {
  return isMovesRow(row.get());
}

export namespace MoveListComponent {
  export type NodeModel = Game.Move.Node | null;

  export type Row<T = MoveListNode[] | Comment> = {
    content: T;
    depth: number;
    renderKey: string;
    rowBgColor?: 'light-row' | 'dark-row';
  };

  export type MoveListNode = {
    beforeComment?: string;
    node: Game.Move.Node;
    afterComment?: string;
    inlineVariations?: MoveListNode[][];
  };

  export type Comment = {
    comment: string;
    node: Game.Move.Node | null;
    lineNumber?: number;
    isBeforeComment?: boolean;
  };

  export interface Params {
    game: GameAPI;
    target: HTMLElement;
    scrollContainer?: HTMLElement;
    hideTimestampText?: boolean;
    displayTimeInClockFormat: boolean;
    showTimestamps: boolean;
    translations: MoveList.Translations;
    customColorsEnabled?: boolean;
  }

  export enum VariationVisibility {
    All = 'all',
    None = 'none',
    Active = 'active',
  }

  export interface API {
    destroy: () => void;
    loadLine: (lines: JCE.Line[] | undefined, result: string, selectedNode?: NodeModel) => void;
    addMainLineNode: (node: NodeModel, selectedNode: NodeModel) => void;
    selectMainLineStart: () => void;
    selectNode: (node?: NodeModel) => void;
    deselectNode: () => void;
    setResult: StoreActions['setResult'];
    setTextType: (displayType: Board.MoveListDisplayTypes) => void;
    setBaseTime: (baseTime: number | undefined) => void;
    setContextMenuEnabled: (enabled: boolean) => void;
    setActiveStatus: (status: boolean) => void;
    isActive: () => boolean;
    addContextMenuActions(actions: ContextMenu.Action[]): void;
    addContextMenuActions(action: ContextMenu.Action): void;
    removeContextMenuActions(actionNames: string[]): void;
    removeContextMenuActions(actionName: string): void;
    setAnalysisHidden: (hidden: boolean) => void;
    setAnnotationsHidden: (hidden: boolean) => void;
    setCommentsHidden: (hidden: boolean) => void;
    setCommentsEditable: (enable: boolean) => void;
    /**
     * @deprecated use setVariationVisibility instead
     */
    setVariationsHidden: (hidden: boolean) => void;
    setVariationVisibility: (visibility: VariationVisibility) => void;
    getVariationVisibility: () => VariationVisibility;
    getMoveNodeEl: (line: number, move: number) => Element | undefined;
    openVariationsPopover: () => void;
    closeVariationsPopover: () => void;
    enableNodeLimits: () => void;
    updateNodeLimits: (nodeLimits: NodeLimits) => void;
    getNodeLimits: () => Record<keyof NodeLimits, JCE.Node.Model | null>;
    setCustomColorsEnabled: (enabled: boolean) => void;
    reinitialize: () => void;
    getQueue: () => Queue.API;
    refreshNode: (node: JCE.Node.Model) => void;
    setClassificationStyle: (style?: Board.MoveListClassificationStyles) => void;
    setNodeEffectOverrides: (overrides: NodeEffectOverridesCollection) => void;
    getNodeEffectOverrides: () => NodeEffectOverridesCollection;
  }

  export type NodeEffectOverridesCollection = {
    [key: string]: NodeEffectOverride;
  };

  export type NodeEffectOverride = {
    effectType?: EffectTypes | null;
    textColor?: string | null;
  };

  export interface Context {
    // game: GameAPI.ExtendedAPI;
    game: GameAPI;
    actions: StoreActions;
    data: Store;
  }

  export interface Props {
    data: Store;
    eventHandlers: EventHandlers;
    hideTimestampText?: boolean;
    displayTimeInClockFormat: boolean;
    showTimestamps: boolean;
    translations: MoveList.Translations;
  }

  export enum Classes {
    Component = 'move-list',
  }

  export const enum CSSVariables {
    TimeBarWidth = '--timeBarWidth',
    TimeValue = '--timeValue',
    MaxMoveTimeTaken = '--timeMaxValue',
    TimestampWidth = '--timestampWidth',
  }

  export const enum ClassNames {
    WithBaseTime = 'timestamps-with-base-time',
    ToggleTimestamps = 'toggle-timestamps',
  }

  export const enum Attributes {
    Node = 'data-node',
    Time = 'data-time',
    WholeMoveNumber = 'data-whole-move-number',
    VMLElement = 'data-vml-element',
  }

  export const Constants = {
    CommentUpdateDelay: 500,
  };
}
